/**
 * PurchaseInterceptionModal Component
 *
 * Full-screen mobile-optimized modal for real-time purchase decisions.
 * Appears when a purchase is intercepted during shopping.
 *
 * Features:
 * - Full-screen modal on mobile (bottom sheet pattern)
 * - Traffic light decision display
 * - Purchase impact visualization
 * - Quick action buttons
 * - Merchant and category context
 * - Swipe gestures for actions
 * - Landscape and portrait support
 */

import { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Modal,
  Slide,
  Chip,
  IconButton,
  useMediaQuery,
  useTheme,
  keyframes,
  Fade,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LocalGroceryStoreIcon from '@mui/icons-material/LocalGroceryStore';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import MovieIcon from '@mui/icons-material/Movie';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import HomeIcon from '@mui/icons-material/Home';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import FlightIcon from '@mui/icons-material/Flight';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';

import TrafficLightDisplay, { type TrafficLightDecision } from './TrafficLightDisplay';
import PurchaseImpact, { type PurchaseImpactData } from './PurchaseImpact';
import QuickActions, { type PurchaseAction } from './QuickActions';

// Category icon mapping
const categoryIcons: Record<string, React.ElementType> = {
  groceries: LocalGroceryStoreIcon,
  grocery: LocalGroceryStoreIcon,
  restaurant: RestaurantIcon,
  dining: RestaurantIcon,
  food: RestaurantIcon,
  entertainment: MovieIcon,
  shopping: ShoppingBagIcon,
  retail: ShoppingBagIcon,
  gas: LocalGasStationIcon,
  fuel: LocalGasStationIcon,
  health: HealthAndSafetyIcon,
  medical: HealthAndSafetyIcon,
  home: HomeIcon,
  housing: HomeIcon,
  auto: DirectionsCarIcon,
  car: DirectionsCarIcon,
  travel: FlightIcon,
  gaming: SportsEsportsIcon,
  games: SportsEsportsIcon,
};

// Pulse border animation for urgent decisions
const urgentPulse = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4);
  }
  50% {
    box-shadow: 0 0 0 10px rgba(239, 68, 68, 0);
  }
`;

// Slide up animation for content
const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export interface PurchaseData {
  id: string;
  merchantName: string;
  amount: number;
  category?: string;
  description?: string;
}

export interface TrafficLightResult {
  decision: TrafficLightDecision;
  confidence: number;
  reasoning: string;
  impact: PurchaseImpactData;
  recommendation?: string;
  alternatives?: string[];
}

export interface PurchaseInterceptionModalProps {
  open: boolean;
  onClose: () => void;
  purchase: PurchaseData | null;
  decision: TrafficLightResult | null;
  isLoading?: boolean;
  currentBalance?: number;
  onAction: (action: PurchaseAction, purchaseId: string) => Promise<void> | void;
}

export function PurchaseInterceptionModal({
  open,
  onClose,
  purchase,
  decision,
  isLoading = false,
  currentBalance = 0,
  onAction,
}: PurchaseInterceptionModalProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isLandscape = useMediaQuery('(orientation: landscape) and (max-height: 500px)');
  const [actionLoading, setActionLoading] = useState(false);

  // Get category icon
  const CategoryIcon = purchase?.category
    ? categoryIcons[purchase.category.toLowerCase()] || ShoppingBagIcon
    : ShoppingBagIcon;

  // Map decision to recommended action
  const getRecommendedAction = (): PurchaseAction | undefined => {
    if (!decision) return undefined;
    switch (decision.decision) {
      case 'green':
        return 'approve';
      case 'yellow':
        return 'delay';
      case 'red':
        return 'decline';
      default:
        return undefined;
    }
  };

  // Handle action with loading state
  const handleAction = useCallback(
    async (action: PurchaseAction) => {
      if (!purchase) return;

      setActionLoading(true);
      try {
        await onAction(action, purchase.id);
        // Auto-close after successful action
        setTimeout(() => {
          onClose();
        }, 500);
      } finally {
        setActionLoading(false);
      }
    },
    [purchase, onAction, onClose]
  );

  // Handle keyboard shortcuts
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (isLoading || actionLoading) return;

      switch (e.key.toLowerCase()) {
        case 'a':
          handleAction('approve');
          break;
        case 'w':
          handleAction('delay');
          break;
        case 'd':
          handleAction('decline');
          break;
        case 'escape':
          onClose();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, isLoading, actionLoading, handleAction, onClose]);

  // Determine if this is an urgent/critical decision
  const isUrgent = decision?.decision === 'red' && decision.confidence > 0.8;

  if (!purchase) return null;

  // Content component (shared between mobile and desktop layouts)
  const ModalContent = (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: isLandscape ? 'row' : 'column',
        overflow: 'auto',
        backgroundColor: 'background.default',
        ...(isUrgent && {
          animation: `${urgentPulse} 2s ease-in-out infinite`,
        }),
      }}
    >
      {/* Header - Close button and merchant info */}
      <Box
        sx={{
          position: isLandscape ? 'relative' : 'sticky',
          top: 0,
          zIndex: 1,
          backgroundColor: 'background.default',
          borderBottom: isLandscape ? 'none' : '1px solid',
          borderColor: 'divider',
          p: 2,
          flexShrink: 0,
          ...(isLandscape && {
            width: '40%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            borderRight: '1px solid',
          }),
        }}
      >
        {/* Close button */}
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 2,
          }}
          aria-label="Close"
        >
          <CloseIcon />
        </IconButton>

        {/* Merchant info */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: isLandscape ? 'column' : 'row',
            alignItems: 'center',
            gap: 2,
            pr: 5, // Space for close button
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              backgroundColor: 'action.hover',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <CategoryIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          </Box>

          <Box sx={{ textAlign: isLandscape ? 'center' : 'left', minWidth: 0 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {purchase.merchantName}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexWrap: 'wrap',
                justifyContent: isLandscape ? 'center' : 'flex-start',
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  color: 'primary.main',
                  fontFamily: 'monospace',
                }}
              >
                ${purchase.amount.toFixed(2)}
              </Typography>
              {purchase.category && (
                <Chip
                  label={purchase.category}
                  size="small"
                  sx={{ textTransform: 'capitalize' }}
                />
              )}
            </Box>
          </Box>
        </Box>

        {/* Traffic Light - In landscape, show in left panel */}
        {isLandscape && (
          <Box sx={{ mt: 3, width: '100%' }}>
            <TrafficLightDisplay
              decision={decision?.decision || null}
              confidence={decision?.confidence || 0}
              isLoading={isLoading}
              size="medium"
            />
          </Box>
        )}
      </Box>

      {/* Main content area */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          p: 2,
          gap: 3,
          overflow: 'auto',
          animation: `${slideUp} 0.3s ease`,
          ...(isLandscape && {
            width: '60%',
            justifyContent: 'center',
          }),
        }}
      >
        {/* Traffic Light Display - Portrait only */}
        {!isLandscape && (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <TrafficLightDisplay
              decision={decision?.decision || null}
              confidence={decision?.confidence || 0}
              isLoading={isLoading}
              size={isMobile ? 'medium' : 'large'}
            />
          </Box>
        )}

        {/* Reasoning */}
        {decision?.reasoning && !isLoading && (
          <Fade in>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                backgroundColor: 'action.hover',
                textAlign: 'center',
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontStyle: 'italic',
                  color: 'text.secondary',
                  lineHeight: 1.6,
                }}
              >
                "{decision.reasoning}"
              </Typography>
            </Box>
          </Fade>
        )}

        {/* Purchase Impact */}
        {decision?.impact && !isLoading && (
          <PurchaseImpact
            impact={decision.impact}
            currentBalance={currentBalance}
            purchaseAmount={purchase.amount}
            compact={isLandscape || isMobile}
          />
        )}

        {/* Alternatives (if any) */}
        {decision?.alternatives && decision.alternatives.length > 0 && !isLoading && (
          <Box sx={{ mt: -1 }}>
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', fontWeight: 600 }}
            >
              Alternatives to consider:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
              {decision.alternatives.slice(0, 3).map((alt, idx) => (
                <Chip
                  key={idx}
                  label={alt}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.75rem' }}
                />
              ))}
            </Box>
          </Box>
        )}
      </Box>

      {/* Action buttons - Fixed at bottom */}
      <Box
        sx={{
          position: isLandscape ? 'absolute' : 'sticky',
          bottom: 0,
          left: isLandscape ? '40%' : 0,
          right: 0,
          p: 2,
          pt: 3,
          pb: isMobile ? 4 : 3, // Extra padding for mobile home indicator
          backgroundColor: 'background.default',
          borderTop: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.1)',
        }}
      >
        <QuickActions
          onAction={handleAction}
          isLoading={actionLoading || isLoading}
          disabled={!decision}
          recommendedAction={getRecommendedAction()}
        />
      </Box>
    </Box>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      disableAutoFocus
      sx={{
        display: 'flex',
        alignItems: isMobile ? 'flex-end' : 'center',
        justifyContent: 'center',
      }}
    >
      <Slide direction={isMobile ? 'up' : 'down'} in={open}>
        <Box
          sx={{
            width: isMobile ? '100%' : 'min(500px, 90vw)',
            height: isMobile ? '95vh' : 'auto',
            maxHeight: isMobile ? '95vh' : '90vh',
            backgroundColor: 'background.paper',
            borderRadius: isMobile ? '24px 24px 0 0' : 3,
            outline: 'none',
            overflow: 'hidden',
            boxShadow: 24,
          }}
        >
          {ModalContent}
        </Box>
      </Slide>
    </Modal>
  );
}

export default PurchaseInterceptionModal;
